import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { PlusIcon, TrashIcon, DocumentTextIcon, ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export default function Index({ material, flash }) {
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const invoices = material.invoices || [];
    const reqNumber = material.formatted_number || String(material.number).padStart(4, '0');

    const statusBadge = (s) => {
        const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'Unknown'}</Badge>;
    };

    const totalInvoiced = invoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
    const totalRequisition = material.total_price || material.items?.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.price)), 0) || 0;

    return (
        <AppLayout>
            <Head title={`Invoices - #${reqNumber}`} />
            <PageHeader
                title="Invoices"
                subtitle={
                    <span className="flex items-center gap-2">
                        Requisition #{reqNumber} — {material.supplier_name || ''}
                        {statusBadge(material.status)}
                    </span>
                }
                actions={
                    <div className="flex gap-2">
                        <Button href="/materials" variant="outline" size="sm">
                            <ArrowLeftIcon className="w-4 h-4" /> Back
                        </Button>
                        <a href={`/materials/${material.id}/invoice-report-pdf`} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors">
                            <ArrowDownTrayIcon className="w-4 h-4" /> Export PDF
                        </a>
                        {material.status === 'approved' && (
                            <Button href={`/materials/${material.id}/invoices/create`} size="sm">
                                <PlusIcon className="w-4 h-4" /> Add Invoice
                            </Button>
                        )}
                    </div>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            {/* Summary Card */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Card>
                    <CardBody className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Requisition Total</p>
                        <p className="text-xl font-bold text-slate-800 mt-1">AED {Number(totalRequisition).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </CardBody>
                </Card>
                <Card>
                    <CardBody className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total Invoiced</p>
                        <p className="text-xl font-bold text-green-600 mt-1">AED {totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </CardBody>
                </Card>
                <Card>
                    <CardBody className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Remaining</p>
                        <p className="text-xl font-bold text-amber-600 mt-1">AED {(totalRequisition - totalInvoiced).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </CardBody>
                </Card>
            </div>

            <Card>
                <Table minWidth="600px">
                    <thead>
                        <tr>
                            <Th>#</Th>
                            <Th>Invoice No.</Th>
                            <Th>Sub Amount</Th>
                            <Th>Tax</Th>
                            <Th>Total</Th>
                            <Th>Date</Th>
                            <Th>Notes</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map((inv, idx) => (
                            <Tr key={inv.id}>
                                <Td className="text-slate-400 text-xs">{idx + 1}</Td>
                                <Td className="font-medium text-slate-800">{inv.invoice_number}</Td>
                                <Td className="whitespace-nowrap">AED {Number(inv.sub_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Td>
                                <Td className="whitespace-nowrap">AED {Number(inv.tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Td>
                                <Td className="font-medium whitespace-nowrap">AED {Number(inv.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Td>
                                <Td className="text-slate-500 text-xs whitespace-nowrap">
                                    {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}
                                </Td>
                                <Td className="text-slate-500 text-sm max-w-[200px] truncate">{inv.notes || '—'}</Td>
                                <Td>
                                    <div className="flex gap-1">
                                        {inv.invoice_file && (
                                            <a href={`/storage/${inv.invoice_file}`} target="_blank" className="p-1.5 hover:bg-emerald-50 rounded-lg" title="Download file">
                                                <ArrowDownTrayIcon className="w-4 h-4 text-emerald-700" />
                                            </a>
                                        )}
                                        <button onClick={() => setDeleteConfirm(inv.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete">
                                            <TrashIcon className="w-4 h-4 text-red-400" />
                                        </button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {invoices.length === 0 && (
                    <EmptyState
                        icon={DocumentTextIcon}
                        title="No invoices yet"
                        description={material.status === 'approved'
                            ? "Add the first invoice for this requisition."
                            : "Invoices can only be added to approved requisitions."
                        }
                        action={material.status === 'approved' ? (
                            <Button href={`/materials/${material.id}/invoices/create`} size="sm">
                                <PlusIcon className="w-4 h-4" /> Add Invoice
                            </Button>
                        ) : null}
                    />
                )}
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Invoice"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">Are you sure you want to delete this invoice? This action cannot be undone.</p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => { router.delete(`/materials/${material.id}/invoices/${deleteConfirm}`); setDeleteConfirm(null); }}>Delete</Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
